// Renders every sponsor-logo block on the page from the admin booth registry.
//
// One renderer, several surfaces: the footer pyramid (sections/event-sponsors)
// and the 協賛 strip under the event header (sections/event-header). Each
// surface is a server-rendered root:
//
//   <div data-event-sponsors data-api-base data-event-id data-identity
//        [data-levels="sponsor"] hidden>
//     <div class="event-sponsors__levels"></div>
//   </div>
//
// data-event-id is the registry event (booth_registry.event_id); data-identity
// is the header identity being rendered (the `event` metaobject handle). The API
// resolves every level — including which title sponsor (冠協賛) heads this
// identity — so rows are simply LOGO_LEVELS in order. data-levels limits which
// rows render (default: all). The root stays hidden when nothing is left.
//
// fetchPublicSponsors is memoised per (apiBase, eventId, identity), so every
// surface — and assets/title-sponsor.js — shares one request per page.
import { fetchPublicSponsors, LOGO_LEVELS } from '@fujigravel/api-contract';

for (const root of document.querySelectorAll('[data-event-sponsors]')) {
  render(root);
}

async function render(root) {
  const { apiBase, eventId, identity, levels } = root.dataset;
  const container = root.querySelector('.event-sponsors__levels');
  if (!apiBase || !eventId || !container) return;

  let sponsors;
  try {
    ({ sponsors } = await fetchPublicSponsors(apiBase, eventId, { identity }));
  } catch (err) {
    console.error('[event-sponsors] fetch failed', err);
    return;
  }

  const wanted = levels ? levels.split(',').map((l) => l.trim()) : LOGO_LEVELS;
  const rows = LOGO_LEVELS
    .filter((level) => wanted.includes(level))
    .map((level) => [level, sponsors.filter((s) => s.level === level)])
    .filter(([, items]) => items.length > 0);
  if (rows.length === 0) return;

  for (const [level, items] of rows) {
    const ul = document.createElement('ul');
    ul.className = `event-sponsors__level event-sponsors__level--${level}`;
    ul.setAttribute('role', 'list');
    for (const s of items) ul.appendChild(renderLogo(s));
    container.appendChild(ul);
  }
  root.hidden = false;
}

// Every entry is the same bordered box. The panel colour follows the logo:
// logo_theme 'dark' = a light logo on transparency → black panel; anything
// else (a dark logo, an opaque logo, unknown) → white. No logo yet → the
// display name, so a sponsor never silently drops off.
function renderLogo(s) {
  const li = document.createElement('li');
  li.className = `event-sponsors__logo event-sponsors__logo--${s.logo_theme === 'dark' ? 'dark' : 'light'}`;
  const name = s.listed_name || s.exhibitor_name || '';

  let content;
  if (s.logo_url) {
    content = document.createElement('img');
    content.src = s.logo_url;
    content.alt = name;
    content.loading = 'lazy';
    content.decoding = 'async';
  } else {
    content = document.createElement('span');
    content.className = 'event-sponsors__name';
    content.textContent = name;
  }

  if (!s.website) {
    li.appendChild(content);
    return li;
  }
  const a = document.createElement('a');
  a.href = s.website;
  a.target = '_blank';
  a.rel = 'noopener';
  a.appendChild(content);
  li.appendChild(a);
  return li;
}
